import {
  LuxTypography,
  LuxCalendar,
  TimeSlot,
  LuxLoader,
  LuxTooltip,
  InformationIcon,
} from '@gemini/fe-ds-core';
import { t } from 'i18next';
import {
  CalendarSection,
  MultipatientsSublabelWrapper,
} from './CalendarPage.styles';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Patient, usePatientStore } from '@store/patientStore';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import useGetCalendarDays from '@hooks/useGetCalendarDays';
import { add } from '@gemini/fe-utils';
import { STATUS_ALERT } from './CalendarPage';
import dayjs from 'dayjs';
import { AvailableDaysResponse } from '@utils/interfaces';
import MultipatientsTags from './MultipatientsTags';

const isSameCalendarDay = (
  a?: string | Date | dayjs.Dayjs,
  b?: string | Date | dayjs.Dayjs,
) => {
  if (!a || !b) return false;
  return dayjs(a).format('YYYY-MM-DD') === dayjs(b).format('YYYY-MM-DD');
};

export interface CalendarComponentProps {
  selectedDate?: Date;
  setSelectedDate: (d?: Date) => void;
  reservationStatus: STATUS_ALERT;
  setReservationStatus: (s: STATUS_ALERT) => void;
  slotsAreLoading: boolean;
  useExternalInsuranceSelectedValue?: boolean;
  insuranceSelectedValue?: string;
}

export const MultipatientsPopup = ({
  isSelectedDay = false,
  patients,
}: {
  isSelectedDay: boolean;
  patients: string[];
}) => {
  if (patients.length === 1) {
    return patients[0];
  }

  return (
    <LuxTooltip
      background="#001625"
      placement="top"
      maxHeight="100px"
      maxWidth="400px"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 32],
              },
            },
          ],
        },
      }}
      title={
        <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
          {patients.map((name, index) => (
            <span
              key={index}
              style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </span>
          ))}
        </div>
      }
    >
      <MultipatientsSublabelWrapper>
        {patients.length + ' patients'}
        <InformationIcon color={isSelectedDay ? 'white' : '#001625'} />
      </MultipatientsSublabelWrapper>
    </LuxTooltip>
  );
};

export const CalendarComponent = (props: CalendarComponentProps) => {
  //Patient Store
  const getPatients = usePatientStore(state => state.getPatients);
  const patients = getPatients();
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const locationId = useOnlineBookingStore(
    state => state.generalInfo?.location,
  );
  const providerId = useOnlineBookingStore(state => state.providerIdFromUrl);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  // used for choose new slot (type, ruleUnit, ruleTime)
  const setChooseNewSlot = usePatientStore(
    state => state.setChooseNewSlotScheduleExam,
  );
  const brandName = useOnlineBookingStore(state => state.brandName);

  const [daysToShow, setDaysToShow] = useState<Array<TimeSlot> | undefined>();
  const [daysOfMonth, setDaysOfMonth] = useState<Date[]>([]);
  const [updatedSelectedDate, setUpdatedSelectedDate] = useState<Date>();
  const calendarClicked = useRef<boolean>(false);

  const currentPatient: Patient | undefined = useMemo(() => {
    return patients && patients[currentPatientIndex];
  }, [patients, currentPatientIndex]);

  const appointmentTypeValue: string | undefined = useMemo(() => {
    return currentPatient?.appointmentType?.appointmentType;
  }, [currentPatient]);

  const insuranceSelectedValue: string | undefined = useMemo(() => {
    if (props.useExternalInsuranceSelectedValue) {
      return props.insuranceSelectedValue;
    }

    const insuranceDrivesAvailability =
      !!formConfigs?.askForInsuranceInformation &&
      formConfigs?.insuranceMustDriveAvailability === true;

    if (!insuranceDrivesAvailability) return '';

    return (
      currentPatient?.insurance?.other || currentPatient?.insurance?.id || ''
    );
  }, [
    currentPatient,
    formConfigs,
    props.insuranceSelectedValue,
    props.useExternalInsuranceSelectedValue,
  ]);

  // get available days
  const daysQuery = useGetCalendarDays({
    practiceIdValue: practiceId,
    appointmentTypeValue,
    insuranceSelectedValue,
    locationIdValue: locationId,
    practitionerIdValue: providerId,
    reservationStatusValue: props.reservationStatus,
  });
  const {
    data: days,
    // refetch: getDays,
    isFetching: getDaysIsFetching,
    // isLoading: getDaysIsLoading,
  } = daysQuery;

  const onSelectDay = useCallback(
    (e: TimeSlot) => {
      props.setSelectedDate(e.date);
      calendarClicked.current = true;
    },
    [currentPatientIndex],
  );

  useEffect(() => {
    setUpdatedSelectedDate(props.selectedDate);
  }, [props.selectedDate]);

  useEffect(() => {
    const calendarIsLoading = getDaysIsFetching;
    const slotsAreLoading = props.slotsAreLoading;

    if (!calendarIsLoading && !slotsAreLoading && calendarClicked.current) {
      document
        ?.getElementById('hour-slots-container')
        ?.scrollIntoView({ behavior: 'smooth' });
      calendarClicked.current = false;
    }
  }, [props.slotsAreLoading, getDaysIsFetching]);

  const renderNewMonth = (month: number, year: number) => {
    if (!days) return;
    if (daysOfMonth.length > 0 && daysOfMonth[0].getMonth() === month) return;
    console.log('#CalendarComponent | renderNewMonth');

    // Mese in JS è 0-indexed. Per ottenere i giorni del mese corrente,
    // si usa month + 1 con giorno 0 (ovvero l'ultimo giorno del mese richiesto).
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const newDays: Date[] = Array.from(
      { length: daysInMonth },
      (_, i) => new Date(year, month, i + 1),
    );

    setDaysOfMonth(newDays);
  };

  const getDefaultMaxDay = useCallback(() => {
    if (days?.aheadNumber && days?.aheadWeeksOrMonths) {
      if (days.aheadWeeksOrMonths === 'WEEKS')
        return add(new Date(), Number(days?.aheadNumber) * 7, 'days');
      return add(new Date(), Number(days?.aheadNumber), 'months');
    }
    return new Date();
  }, [days]);

  const searchFirstDayAvailable = (days: AvailableDaysResponse) => {
    const firstDayAvailable = days.availableDays.find(d => d.available);
    if (firstDayAvailable) {
      console.log(
        '#Calendar | new date available founded: ',
        firstDayAvailable.day,
      );
      const firstDayAvailableJs = dayjs(firstDayAvailable.day);
      renderNewMonth(firstDayAvailableJs.month(), firstDayAvailableJs.year());
      props.setSelectedDate(firstDayAvailable.day);
    } else {
      console.log('#Calendar | no days available');
      props.setSelectedDate(undefined);
    }
  };

  useEffect(() => {
    console.log(
      '#CalendarComponent | #useEffect days: ',
      !!(days && daysOfMonth.length > 0 && !getDaysIsFetching),
    );
    if (days && daysOfMonth.length > 0 && !getDaysIsFetching) {
      setChooseNewSlot(currentPatientIndex, {
        ruleTime: days?.processingTime || days.aheadNumber,
        ruleUnit: days?.unitOfMeasure || days.aheadWeeksOrMonths,
      });

      const selectedDateFromApi = props.selectedDate
        ? days.availableDays.find(d =>
            isSameCalendarDay(d.day, props.selectedDate),
          )
        : undefined;

      if (
        props.selectedDate &&
        props.reservationStatus === STATUS_ALERT.HAPPY_FLOW &&
        selectedDateFromApi?.available !== true
      ) {
        searchFirstDayAvailable(days);
      }

      // calc daysToShow
      let isFirstSelection = true;
      const toSave = daysOfMonth.map(day => {
        const dayFromApi = days?.availableDays?.find(d =>
          isSameCalendarDay(d.day, day),
        );

        if (props.selectedDate) {
          if (isSameCalendarDay(props.selectedDate, day)) {
            if (
              dayFromApi &&
              props.reservationStatus === STATUS_ALERT.GET_DAYS_TO_VERIFY
            ) {
              console.log(
                '#CalendarComponent | use case user changed appointment type or insurance, and the day is present in the service: ',
                dayFromApi,
              );
              props.setReservationStatus(
                dayFromApi.available
                  ? STATUS_ALERT.DAY_AVAILABLE
                  : STATUS_ALERT.DAY_NOT_AVAILABLE,
              );
              searchFirstDayAvailable(days);
            } else if (
              !dayFromApi &&
              props.reservationStatus === STATUS_ALERT.GET_DAYS_TO_VERIFY
            ) {
              console.log(
                '#CalendarComponent | use case user changed appointment type or insurance, and the day is not present in the service, we need to search first day available: ',
              );
              searchFirstDayAvailable(days);
              props.setReservationStatus(STATUS_ALERT.DAY_NOT_AVAILABLE);
            } else if (props.reservationStatus === STATUS_ALERT.HAPPY_FLOW) {
              console.log(
                '#CalendarComponent | use case return in this page: ',
              );
              isFirstSelection = false;
            }
          } else if (props.reservationStatus === STATUS_ALERT.HAPPY_FLOW) {
            // console.log(
            //   '#CalendarComponent | use case change month or seleted day is different',
            // );
            isFirstSelection = false;
          }
        } else if (
          dayFromApi &&
          dayFromApi.available &&
          isFirstSelection &&
          props.reservationStatus === STATUS_ALERT.HAPPY_FLOW
        ) {
          console.log(
            '#CalendarComponent | selection day automatically for the current month',
            dayFromApi.available,
          );
          isFirstSelection = false;
          props.setSelectedDate(dayFromApi.day);
        }

        const status =
          (dayFromApi && !dayFromApi.available) || !dayFromApi
            ? 'disabled'
            : 'default';

        const sublabel = multipatientSelectedDaySublabel(day);

        return {
          date: day,
          templateDate: 'D',
          sublabel,
          status,
        } as TimeSlot;
      });

      if (
        isFirstSelection && // we know
        props.reservationStatus === STATUS_ALERT.HAPPY_FLOW
      ) {
        console.log(
          '#Calendar | date not selected automatically, search first day available',
        );
        searchFirstDayAvailable(days);
      }
      setDaysToShow(toSave);
    }
  }, [
    days,
    daysOfMonth,
    getDaysIsFetching,
    currentPatientIndex,
    updatedSelectedDate,
  ]);

  const getOtherPatientsSelectedDays = useCallback(() => {
    return patients
      .filter(
        (patient, index) =>
          index !== currentPatientIndex &&
          patient &&
          patient?.scheduleExam?.day,
      )
      .map(patient => {
        const firstName = patient?.patientDetails?.firstName;
        const lastName = patient?.patientDetails?.lastName;

        return {
          patientName:
            firstName && lastName ? `${firstName} ${lastName}` : 'New patient',
          day: patient?.scheduleExam?.day,
        };
      });
  }, [patients, currentPatient]);

  const multipatientSelectedDaySublabel = useCallback(
    (day: Date) => {
      const theDayIsToday = isSameCalendarDay(new Date(), day);

      const matchingPatients = getOtherPatientsSelectedDays()
        .filter(patient => isSameCalendarDay(patient?.day, day))
        .map(patient => {
          return patient.patientName;
        });

      if (matchingPatients.length > 0) {
        return (
          <MultipatientsPopup
            isSelectedDay={isSameCalendarDay(updatedSelectedDate, day)}
            patients={matchingPatients}
          />
        );
      }

      return theDayIsToday ? 'Today' : '';
    },
    [getOtherPatientsSelectedDays, updatedSelectedDate],
  );

  return (
    <CalendarSection isDays isLoading={getDaysIsFetching}>
      {getDaysIsFetching ? (
        <LuxLoader width="100%" />
      ) : (
        <>
          {!brandName && (
            <LuxTypography variant="textMdRegular">
              {t('calendarPage.subtitleDays')
                .replace('#numOfWeeks', days?.aheadNumber || '')
                .replace(
                  '#unit',
                  days?.aheadWeeksOrMonths?.toLocaleLowerCase() || '',
                )}
            </LuxTypography>
          )}
          <LuxCalendar
            variant="date"
            onSelectSlot={onSelectDay}
            listValues={daysToShow || []}
            onChangeMonth={renderNewMonth}
            minDay={days?.minDay ? new Date(days?.minDay) : new Date()}
            maxDay={days?.maxDay ? new Date(days?.maxDay) : getDefaultMaxDay()}
            disableBeforeDates
            selected={props.selectedDate}
            transparentHeader
          />
          <MultipatientsTags />
        </>
      )}
    </CalendarSection>
  );
};
